import * as Yup from "yup";

import { Form, Formik } from "formik";
import {
  NumberField,
  Section,
  SelectField,
  SubmitButton,
} from "@microrealestate/commonui/components";
import { useCallback, useContext, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { StoreContext } from "../../store";
import useTranslation from "next-translate/useTranslation";
import { Box, makeStyles, Typography } from "@material-ui/core";
import { CustomSwitchField } from "@microrealestate/commonui/components/FormFields/SwitchField";
import { toJS } from "mobx";
import { SettingGridRow } from "../GridRow";
import { usePermissions } from "../../utils/PermissionProvider";
import { NOTIFICATION_TENANT } from "../../constants/permission";

const useStyles = makeStyles(() => ({
  centeredValue: {
    textAlign: "center",
  },
}));

const InvoiceReminderConfig = ({ t, values = {}, disabled }) => {
  const classes = useStyles();
  const { hasPermission } = usePermissions();
  const reminderConfigs = [
    {
      key: "preReminder",
      title: "Invoice",
      description: "Will be sent x days before due",
    },
    {
      key: "firstReminder",
      title: "First reminder",
      description: "Will be sent x days after due",
    },
    {
      key: "secondReminder",
      title: "Second reminder",
      description: "Will be sent x days after due",
    },
    {
      key: "lastReminder",
      title: "Last reminder",
      description: "Will be sent x days after due",
    },
  ];

  const isRestricted = !hasPermission(NOTIFICATION_TENANT)

  const preReminderConfig = reminderConfigs[0];
  const otherReminderConfigs = reminderConfigs.slice(1);
  const premiumOnlyTooltip = t("This feature is not available for your plan");

  return (
    <Box display="flex" flexDirection="column" mb={1}>
      <Box display="flex" alignItems="flex-start" mb={1.5}>
        <CustomSwitchField name="invoiceReminder.enabled" />

        <Box ml={1} width="100%">
          <Typography variant="subtitle1">{t("Invoice reminder")}</Typography>
          <Typography variant="caption" color="textSecondary">
            {t("Automatically remind tenants about upcoming or overdue invoices")}
          </Typography>

          <Box mt={1}>
            {/* PreReminder - always enabled */}
            {preReminderConfig && (() => {
              const { key, title, description } = preReminderConfig;
              const prefix = `invoiceReminder.${key}`;
              const isEnabled = values.invoiceReminder[key].enabled;
              return (
                <SettingGridRow
                  key={key}
                  disabled={disabled}
                  left={
                    <Box display="flex" alignItems="flex-start">
                      <CustomSwitchField
                        color="primary"
                        name={`${prefix}.enabled`}
                        disabled={disabled}
                      />

                      <Box ml={1}>
                        <Typography variant="subtitle1">{t(title)}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t(description)}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  right={
                    <NumberField
                      inputProps={{
                        className: classes.centeredValue,
                      }}
                      disabled={disabled || !isEnabled}
                      name={`${prefix}.xDay`}
                    />
                  }
                />
              );
            })()}

            {isRestricted && (
              <Box mb={1} p={1} bgcolor="action.hover" borderRadius={4}>
                <Typography variant="caption" color="textSecondary" style={{ fontStyle: 'italic' }}>
                  {premiumOnlyTooltip}
                </Typography>
              </Box>
            )}

            <Box>
              {otherReminderConfigs.map(({ key, title, description }) => {
                const prefix = `invoiceReminder.${key}`;
                const isEnabled = values.invoiceReminder[key].enabled;
                return (
                  <SettingGridRow
                    key={key}
                    disabled={isRestricted || disabled}
                    left={
                      <Box display="flex" alignItems="flex-start">
                        <CustomSwitchField
                          color="primary"
                          name={`${prefix}.enabled`}
                          disabled={isRestricted || disabled}
                        />

                        <Box ml={1}>
                          <Typography variant="subtitle1">{t(title)}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {t(description)}
                          </Typography>
                        </Box>
                      </Box>
                    }
                    right={
                      <NumberField
                        inputProps={{
                          className: classes.centeredValue,
                        }}
                        disabled={isRestricted || disabled || !isEnabled}
                        name={`${prefix}.xDay`}
                      />
                    }
                  />
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const RecapReportConfig = ({ values = {}, disabled, t }) => {
  const notificationConfigs = [
    {
      key: "tenantEntry",
      title: "Recap tenant entry",
      description: "Summary of the latest tenant entry within the selected period",
    },
    {
      key: "paymentReceived",
      title: "Recap payment received",
      description: "Summary of the latest payment received within the selected period",
    },
    {
      key: "invoiceSent",
      title: "Recap invoice sent",
      description: "Summary of the invoice sent during the chosen period",
    },
    {
      key: "invoiceDue",
      title: "Recap invoice due",
      description: "Summary of the invoice due in the selected timeframe",
    },
  ];

  const timeRanges = [
    { id: "days", name: "Daily" },
    { id: "weeks", name: "Weekly" },
  ];

  return (
    <Box display="flex" flexDirection="column">
      <Box display="flex" alignItems="flex-start" mb={1.5}>
        <CustomSwitchField name="recapReport.enabled" />

        <Box ml={1} width="100%">
          <Typography variant="subtitle1">{t("Report recap")}</Typography>
          <Typography variant="caption" color="textSecondary">
            {t("Automatically send recap of reports")}
          </Typography>

          <Box mt={1}>
            {notificationConfigs.map(({ key, title, description }) => {
              const namePrefix = `recapOf${key.charAt(0).toUpperCase() + key.slice(1)}`;
              const isEnabled = values.recapReport?.[namePrefix]?.enabled;
              return (
                <SettingGridRow
                  key={key}
                  disabled={disabled}
                  left={
                    <Box display="flex" alignItems="flex-start">
                      <CustomSwitchField
                        color="primary"
                        name={`recapReport.${namePrefix}.enabled`}
                        disabled={disabled}
                      />

                      <Box ml={1}>
                        <Typography variant="subtitle1">{t(title)}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t(description)}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  right={
                    <SelectField
                      formMargin="none"
                      name={`recapReport.${namePrefix}.frequency`}
                      values={timeRanges.map(({ id, name }) => ({
                        id,
                        value: id,
                        label: t(name),
                      }))}
                      style={{ margin: 0 }}
                      disabled={disabled || !isEnabled}
                    />
                  }
                />
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const NotificationSettings = observer(({ onSubmit, onSubmitted }) => {
  const { t } = useTranslation("common");
  const store = useContext(StoreContext);

  const initialValues = useMemo(() => {
    const selectedOrg = toJS(store.organization.selected);
    const { invoiceReminder, birthdayReminder, recapReport } = selectedOrg?.notificationSettings;

    return {
      birthdayReminder,
      invoiceReminder,
      recapReport,
    };
  }, [store.organization.selected]);

  const _onSubmit = useCallback(
    async (settings) => {
      const { invoiceReminder, birthdayReminder, recapReport } = settings;
      // console.log(settings);
      const payload = {
        // TODO: use latest schema
        // v2 schema
        notificationSettings: settings,

        // TODO: remove deprecated schema values
        // deprecated schema
        // birthDayNotification: birthdayReminder.enabled,
        // automationNotification: invoiceReminder.enabled,
        // tenantReminderSetting: {
        //   invoice: {
        //     selected: invoiceReminder.preReminder.enabled,
        //     xDayBefore: invoiceReminder.preReminder.xDay,
        //   },
        //   rentcall: {
        //     selected: invoiceReminder.firstReminder.enabled,
        //     xDayAfter: invoiceReminder.firstReminder.xDay,
        //   },
        //   rentcallReminder: {
        //     selected: invoiceReminder.secondReminder.enabled,
        //     xDayAfter: invoiceReminder.secondReminder.xDay,
        //   },
        //   rentcallLastReminder: {
        //     selected: invoiceReminder.lastReminder.enabled,
        //     xDayAfter: invoiceReminder.lastReminder.xDay,
        //   },
        // },
        // landlordReminderSetting: {
        //   dailyReportOfInvoices: recapReport.recapOfInvoiceDue.enabled,
        //   weeklyReportOfInvoices: recapReport.recapOfInvoiceDue.enabled,
        //   dailyReportOfNotification: recapReport.recapOfInvoiceSent.enabled,
        //   newTenantNotification: recapReport.recapOfTenantEntry.enabled,
        //   isPaymentReceived: recapReport.recapOfPaymentReceived.enabled,
        // },
      };
      await onSubmit(payload);
    },
    [onSubmit]
  );

  return (
    <Formik
      initialValues={initialValues}
      // validationSchema={Yup.object().shape({
      //   tenantFinesSetting: Yup.object().shape({
      //     finesPerDay: Yup.number(), // .required(t('Fines per day is a required field')),
      //     finesMaxDay: Yup.number(), // .required(t('Fine after x days due is a required field')),
      //   }),
      //   tenantReminderSetting: Yup.object().shape({
      //     invoice: Yup.object().shape({
      //       xDayBefore: Yup.number().required(
      //         t("Send invoice before x days due is a required field")
      //       ),
      //     }),
      //     rentcall: Yup.object().shape({
      //       xDayAfter: Yup.number().required(
      //         t("Send rentcall after x days due is a required field")
      //       ),
      //     }),
      //     rentcallReminder: Yup.object().shape({
      //       xDayAfter: Yup.number().required(
      //         t("Send rentcall reminder after x days due is a required field")
      //       ),
      //     }),
      //     rentcallLastReminder: Yup.object().shape({
      //       xDayAfter: Yup.number().required(
      //         t("Send rentcall last reminder after x days due is a required field")
      //       ),
      //     }),
      //   }),
      // })}
      onSubmit={_onSubmit}
    >
      {({ values, isSubmitting }) => {
        return (
          <Form autoComplete="off">
            <Section label={t("Tenant Notification")}>
              {/* Birthday Wishes Config */}
              <Box display="flex" alignItems="flex-start" mb={1.5}>
                <CustomSwitchField name={`birthdayReminder.enabled`} color="primary" />
                <Box ml={1}>
                  <Typography variant="subtitle1">{t("Birthday wishes")}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t("Automatically send birthday greetings to your tenants")}
                  </Typography>
                </Box>
              </Box>
              {/* Invoice Reminder Config */}
              <InvoiceReminderConfig
                t={t}
                userPlan={store.user?.plan}
                values={values || initialValues}
                disabled={!values.invoiceReminder.enabled}
              />
            </Section>
            <Section label={t("Landlord & Admin Notification")}>
              {/* Recap Report Config */}
              <RecapReportConfig
                t={t}
                values={values || initialValues}
                disabled={!values.recapReport.enabled}
              />
            </Section>
            <SubmitButton size="large" label={!isSubmitting ? t("Save") : t("Saving")} />
          </Form>
        );
      }}
    </Formik>
  );
});

export default NotificationSettings;
